import { useContext, useState } from 'react';

import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';

import { DocumentUploadSource, generateUploadDocumentAnalyticsEventString } from '../../DocumentCapture.helpers';
import { DocumentCaptureOfferContext } from '../../DocumentCaptureContexts';
import { captureSentryException } from '../../DocumentCaptureError/captureSentryException';
import { displayToast } from '../../DocumentCaptureError/displayToast';
import type { Input } from '../pages/uploadImageReducer.types';
import { documentHasBeenApproved, documentNeedsReupload } from '../UploadDocuments.selectors';
import { ReviewStatus } from '../UploadDocumentsSection/UploadDocumentsSection.types';
import { useDeleteDocument } from '../useDeleteDocument';
import useUpload from '../useUpload';

import { useImageManager } from './useImageManager';
import type { AddImageParams } from './useImageManager.types';

/* eslint-disable import/prefer-default-export */
const matchReviewStatusToFlags = ({
  isApproved,
  isInvalid,
}: { isApproved: boolean; isInvalid: boolean }) => {
  if (isInvalid) {
    return ReviewStatus.actionRequired;
  }

  if (isApproved) {
    return ReviewStatus.passed;
  }

  return ReviewStatus.readyForReview;
};

export const useImageInput = ({ input, onUploadError, onUploadSuccess }:
{ input: Input; onUploadError?: (error: unknown) => void; onUploadSuccess?: (response: AddImageParams) => void }) => {
  const {
    addImage,
    hasRequestedReuploadActions,
    kind,
    removeImage,
    sections,
    uploadDocType,
  } = useImageManager();

  const onGenericUploadError = (err: unknown) => {
    displayToast(err);
    captureSentryException(err);
  };

  const genericSuccessHandler = (response: AddImageParams) => {
    addImage(response);
  };

  const context = useContext(DocumentCaptureOfferContext);
  const { valuation } = context;

  const enquiryId = valuation?.valuation_ref;
  const [thumbnail, setThumbnail] = useState<string | undefined>();

  const { deleteDocument } = useDeleteDocument({
    onError: (err: unknown) =>
      displayToast(err),
    onSuccess: ({ documentId: deletedDocumentId }: { documentId: number }) => {
      removeImage({ documentId: deletedDocumentId });
      setThumbnail(undefined);
    },
  });

  const { loading: loadingUpload, upload } = useUpload({
    onError: onUploadError ?? onGenericUploadError,
    onSuccess: onUploadSuccess ?? genericSuccessHandler,
  });

  const hasReuploadBeenRequested = documentNeedsReupload(input);

  const isBlurred = input?.isSharp === false;

  const isInvalid = documentNeedsReupload(input) || isBlurred;
  const isApproved = !isBlurred && documentHasBeenApproved(input);

  const reviewStatus = matchReviewStatusToFlags({ isApproved, isInvalid });

  const isLoading = Boolean(loadingUpload);

  const handleOnChange = async (file: File | null | undefined, source: DocumentUploadSource = 'uploaded') => {
    const documentId = hasRequestedReuploadActions
      ? input.documentId
      : undefined;
    const label = sections.addAnotherPage ? input.label : undefined;

    if (!file) {
      return;
    }

    try {
      const newPhoto = {
        blobUrl: window.URL.createObjectURL(file),
        enquiryId,
        file,
        id: documentId,
        isReupload: hasReuploadBeenRequested,
        kind: kind as string,
        label: label as string,
        pageNumber: input.pageNumber,
        source,
      };

      setThumbnail(newPhoto.blobUrl);

      await upload(newPhoto);
    } catch (error) {
      onUploadError?.(error);
      window?.Sentry?.captureException?.(new Error(error as string));
    }
  };

  const handleClick = (openFileViewer: () => void) => {
    const message = String(input.name);

    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.DOCUMENTS_UPLOAD_BUTTON_CLICKED(
      {
        label: generateUploadDocumentAnalyticsEventString(
          {
            hasRequestedReuploadActions,
            message,
            uploadDocType,
          },
        ),
      },
    );

    openFileViewer();
  };

  return {
    deleteDocument,
    handleClick,
    handleOnChange,
    isBlurred,
    isLoading,
    previewImage: thumbnail,
    reviewStatus,
  };
};
